// Full nuke for a wedged PWA session.
//
// Wipes EVERY persistence layer the app touches:
//   • Firebase auth (signOut)
//   • Firebase's own IndexedDB (firebaseLocalStorageDb + the v8 fallback)
//   • The custom anon-credential backup (IDB, Cache Storage, cookie, localStorage)
//   • Every Cache Storage entry (workbox runtime, app-shell, audio, etc.)
//   • All service worker registrations
//   • localStorage + sessionStorage
//   • Common auth cookies (auth-token, muuzbox_anon_uid)
//
// Triggers:
//   • window.muuzboxNuke() — from anywhere, including DevTools / parent postMessage
//   • The "Reset app" link on the login screen
//   • URL query string `?muuzbox-nuke=1` — caught at index.js boot before React mounts
//
// Always reloads at the end so the freshly-cleared state can re-initialise.

const deleteIDB = (name) =>
  new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
      setTimeout(resolve, 3000); // hard timeout — a blocked delete can hang
    } catch (_) {
      resolve();
    }
  });

const deleteCookie = (name) => {
  // Cover the common host/path variants — a cookie set on `.muuzbox.com`
  // is different from one set on `muuzbox.com`, etc.
  const hostnames = [
    window.location.hostname,
    '.' + window.location.hostname,
    window.location.hostname.replace(/^[^.]+\./, ''),
    '.' + window.location.hostname.replace(/^[^.]+\./, ''),
  ];
  const paths = ['/', window.location.pathname];
  for (const domain of hostnames) {
    for (const path of paths) {
      document.cookie = `${name}=; Max-Age=0; path=${path}; domain=${domain}`;
    }
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
  document.cookie = `${name}=; Max-Age=0`;
};

export async function resetAllState({ reload = true } = {}) {
  console.warn('[muuzboxNuke] full reset starting…');

  // 1. Firebase signOut — best-effort. We don't depend on auth being mounted.
  try {
    const mod = await import('../firebase-config');
    if (mod?.auth?.signOut) {
      await mod.auth.signOut().catch(() => {});
    }
  } catch (_) {}

  // 2. Service workers — unregister everything so the next load fetches fresh.
  try {
    if (navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch (_) {}

  // 3. Cache Storage — every entry, not just the muuzbox-anon-backup one.
  try {
    if (window.caches?.keys) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
    }
  } catch (_) {}

  // 4. IndexedDB — Firebase auth's databases + the app's anon backup +
  //    the vote cache. Hard-coded list because indexedDB.databases() is
  //    Chromium-only and we need this to work on iOS Safari.
  await Promise.all(
    [
      'firebaseLocalStorageDb',                 // Firebase v9+ auth persistence
      'firebase-installations-database',        // FID
      'firebase-heartbeat-database',            // SDK heartbeat
      'firebase-messaging-database',            // FCM
      'firebaseLocalStorage',                   // legacy v8 name
      'muuzbox-anon-backup',                    // anonCredentialBackup.js IDB store
      'muuzbox-vote-cache',                     // voteCache.js (guessed name)
    ].map(deleteIDB)
  );

  // 5. localStorage + sessionStorage — cheap, fully clear both.
  try { localStorage.clear(); } catch (_) {}
  try { sessionStorage.clear(); } catch (_) {}

  // 6. Cookies known to influence auth flow.
  try {
    deleteCookie('auth-token');
    deleteCookie('muuzbox_anon_uid');
  } catch (_) {}

  // 7. Notify parent (the launcher / admin shell embedding us in an iframe)
  //    so it can mirror the reset on its side. Non-fatal if no parent.
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'muuzbox-force-reset' }, '*');
    }
  } catch (_) {}

  console.warn('[muuzboxNuke] reset complete — reloading');

  if (reload) {
    // Strip the trigger from the URL so the reload doesn't loop.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('muuzbox-nuke');
      // Use replace so back-button doesn't take them to the trigger URL.
      window.location.replace(url.toString());
    } catch (_) {
      window.location.reload();
    }
  }
}

// Make the reset reachable from anywhere — DevTools, an injected debug
// session, or a postMessage("muuzbox-force-reset") from the parent shell.
if (typeof window !== 'undefined') {
  window.muuzboxNuke = resetAllState;
  window.addEventListener('message', (evt) => {
    if (evt?.data?.type === 'muuzbox-force-reset') {
      resetAllState();
    }
  });

  // ─── 5-tap-on-logo escape hatch ────────────────────────────────────
  //
  // Stuck users never reach the login screen (they're authenticated as
  // a broken anon user, so the layout renders but every menu is dead).
  // Document-level click delegation against any element whose src looks
  // like the Muuzbox logo means a user can recover by tapping the logo
  // five times within two seconds — no matter which layout they're on,
  // no URL editing required. Easy to discover when told, invisible
  // otherwise. Counter resets if a tap doesn't follow within 2s.
  let _logoTapCount = 0;
  let _logoTapTimer = null;
  const _LOGO_RE = /muuzbox-logo\.png/i;
  const _bumpLogoTap = () => {
    _logoTapCount += 1;
    if (_logoTapTimer) clearTimeout(_logoTapTimer);
    _logoTapTimer = setTimeout(() => { _logoTapCount = 0; }, 2000);
    if (_logoTapCount >= 5) {
      _logoTapCount = 0;
      const ok = window.confirm(
        'Reset the app?\n\nThis signs you out everywhere and clears all local data. You\'ll need to sign in again.'
      );
      if (ok) resetAllState();
    }
  };
  document.addEventListener('click', (evt) => {
    try {
      const t = evt && evt.target;
      if (!t || t.tagName !== 'IMG') return;
      const src = t.getAttribute('src') || '';
      if (_LOGO_RE.test(src)) _bumpLogoTap();
    } catch (_) {}
  }, true); // capture so it works even if a child stops propagation
}

export default resetAllState;
